import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MyOffersPanel = ({ offers, onViewDeal, currentLanguage }) => {
  const translations = {
    en: {
      myOffers: 'My Offers',
      noOffers: 'No offers submitted',
      startSubmitting: 'Start submitting offers to purchase requests',
      item: 'Item',
      proposedFee: 'Proposed Fee',
      status: 'Status',
      viewDeal: 'View Deal',
      pending: 'Pending',
      accepted: 'Accepted',
      declined: 'Declined'
    },
    ko: {
      myOffers: '내 제안',
      noOffers: '제출된 제안 없음',
      startSubmitting: '구매 요청에 제안을 제출하세요',
      item: '상품',
      proposedFee: '제안 수수료',
      status: '상태',
      viewDeal: '거래 보기',
      pending: '대기 중',
      accepted: '수락됨',
      declined: '거절됨'
    }
  };

  const t = translations?.[currentLanguage];

  const statusConfig = {
    pending: { color: 'text-warning', bg: 'bg-warning/10', label: t?.pending, icon: 'Clock' },
    accepted: { color: 'text-success', bg: 'bg-success/10', label: t?.accepted, icon: 'CheckCircle' },
    declined: { color: 'text-error', bg: 'bg-error/10', label: t?.declined, icon: 'XCircle' }
  };

  const formatCurrency = (amount) => {
    return `${amount?.toLocaleString()}원`;
  };

  if (offers?.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-md p-6 md:p-8 lg:p-12 text-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Send" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
          {t?.noOffers}
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          {t?.startSubmitting}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-md overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={24} color="var(--color-accent)" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
            {t?.myOffers}
          </h2>
        </div>
      </div>
      <div className="divide-y divide-border">
        {offers?.map((offer) => {
          const status = statusConfig?.[offer?.status] || statusConfig?.pending;
          return (
            <div key={offer?.id} className="p-4 md:p-6 hover:bg-muted/30 transition-base">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-1">
                        {offer?.request?.itemName || 'Unknown Item'}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs font-medium rounded caption">
                          {offer?.request?.category}
                        </span>
                        <div className="flex items-center gap-1">
                          <Icon name="MapPin" size={12} color="var(--color-muted-foreground)" />
                          <span className="text-xs text-muted-foreground caption">
                            {offer?.request?.originCountry} → {offer?.request?.destinationCountry}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`px-3 py-1 ${status?.bg} rounded-full flex items-center gap-1`}>
                      <Icon name={status?.icon} size={14} color={`var(--color-${offer?.status === 'pending' ? 'warning' : offer?.status === 'accepted' ? 'success' : 'error'})`} />
                      <span className={`text-xs font-medium ${status?.color} caption`}>
                        {status?.label}
                      </span>
                    </div>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground caption mb-1">Your Message</p>
                    <p className="text-sm text-foreground">{offer?.message}</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Icon name="DollarSign" size={16} color="var(--color-primary)" />
                      <span className="text-sm text-muted-foreground caption">{t?.proposedFee}:</span>
                      <span className="text-base font-semibold text-primary data-text">
                        {formatCurrency(offer?.proposedFee)}
                      </span>
                    </div>
                  </div>
                </div>

                {offer?.status === 'accepted' && (
                  <div className="flex md:flex-col gap-2">
                    <Button
                      variant="default"
                      size="default"
                      iconName="MessageSquare"
                      iconPosition="left"
                      onClick={() => onViewDeal(offer?.id)}
                      fullWidth
                      className="md:w-32"
                    >
                      {t?.viewDeal}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyOffersPanel;